export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiClientError extends Error {
  constructor(public readonly status: number, public readonly payload: ApiErrorPayload) {
    super(payload.message);
    this.name = "ApiClientError";
  }
}

const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API_BASE_URL = `${backendUrl}/api/v1`;

export interface RequestOptions extends RequestInit {
  query?: Record<string, string | number | boolean | undefined>;
  idempotencyKey?: string;
}

let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(provider: (() => Promise<string | null>) | null) {
  tokenProvider = provider;
}

export async function getCurrentToken(): Promise<string | null> {
  if (tokenProvider) {
    try {
      return await tokenProvider();
    } catch {
      return null;
    }
  }
  return null;
}

export function getStoredProjectId(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("aczon_selected_project_id");
}

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const query = new URLSearchParams();
  Object.entries(options.query || {}).forEach(([key, value]) => value !== undefined && query.set(key, String(value)));
  const queryString = query.toString();
  const url = `${API_BASE_URL}${path}${queryString ? `?${queryString}` : ""}`;
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");

  const token = headers.has("Authorization") ? null : await getCurrentToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const projectId = getStoredProjectId();
  if (projectId) {
    headers.set("X-Project-Id", projectId);
  }

  if (options.body && !headers.has("Content-Type") && !(options.body instanceof FormData)) headers.set("Content-Type", "application/json");
  if (options.idempotencyKey) headers.set("Idempotency-Key", options.idempotencyKey);

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (reason) {
    throw new ApiClientError(0, { code: "NETWORK_UNAVAILABLE", message: "The API is unavailable. The operation can be queued for retry.", details: reason });
  }

  const body = await response.json().catch(() => undefined);
  if (!response.ok) {
    const nested = body?.error || body?.detail;
    const payload: ApiErrorPayload = typeof nested === "object" && nested !== null
      ? { code: nested.code || `HTTP_${response.status}`, message: nested.message || "Request failed", details: nested.details }
      : { code: `HTTP_${response.status}`, message: String(nested || response.statusText), details: body };
    throw new ApiClientError(response.status, payload);
  }
  return body as T;
};
